'use client';

import ReactMarkdown from 'react-markdown';
import type { ChatMessage as ChatMessageType } from '@/lib/types';
import StreamingText from './StreamingText';

interface ChatMessageProps {
  message: ChatMessageType;
  isStreaming?: boolean;
}

export default function ChatMessage({ message, isStreaming = false }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`max-w-[80%] px-4 py-3 rounded-2xl ${
          isUser ? 'rounded-br-md' : 'rounded-bl-md'
        }`}
        style={{
          backgroundColor: isUser
            ? 'rgba(74, 125, 189, 0.2)'
            : 'var(--bg-card)',
          border: `1px solid ${
            isUser
              ? 'rgba(74, 125, 189, 0.3)'
              : 'rgba(200, 164, 94, 0.1)'
          }`,
        }}
      >
        {isUser ? (
          <p className="text-sm leading-relaxed">{message.content}</p>
        ) : (
          <div className="text-sm leading-relaxed prose-invert">
            {isStreaming && !message.content ? (
              <StreamingText text="" isStreaming />
            ) : (
              <>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <p className="mb-2 last:mb-0">{children}</p>
                    ),
                    strong: ({ children }) => (
                      <strong style={{ color: 'var(--accent-gold)' }}>
                        {children}
                      </strong>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc pl-4 mb-2">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal pl-4 mb-2">{children}</ol>
                    ),
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                    blockquote: ({ children }) => (
                      <blockquote
                        className="pl-3 my-2 italic"
                        style={{
                          borderLeft: '2px solid var(--accent-gold)',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {children}
                      </blockquote>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
                {isStreaming && <StreamingText text="" isStreaming />}
              </>
            )}
          </div>
        )}

        {/* Inline verse references */}
        {message.verses && message.verses.length > 0 && (
          <div className="mt-2 pt-2 flex flex-wrap gap-1.5" style={{ borderTop: '1px solid rgba(200, 164, 94, 0.1)' }}>
            {message.verses.map((v, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors"
                style={{
                  backgroundColor: 'rgba(200, 164, 94, 0.1)',
                  color: 'var(--accent-gold)',
                  border: '1px solid rgba(200, 164, 94, 0.2)',
                }}
              >
                {v.book} {v.chapter}:{v.verse}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
